import { map } from 'lodash';

import { post } from '@/utils/client';

import { PostTypesType } from '@/types/scheduler/post/postTypeType';
import type { SocialProfileNetworkType } from '@/types/socialProfileType';
import { OptionType } from '@/components/base/Select/types';
import { ALBUM_TYPE_MANUAL } from '@/lib/albums/albumFunnel/types';

import { ALBUMS } from '@/lib/routes/apiEndpoints';
import { TYPE_STORY } from '@/lib/scheduler/postType';
import { INSTAGRAM, THREADS, X } from '@/lib/socialProfile';

type AlbumType = {
    id: number;
    name: string;
    criteria?: { album_type?: string };
};

type SocialProfileType = {
    id: number;
    social_network_type: string;
};

type PostsData = {
    [key: string]: {
        campaign_id?: number | null;
        [key: string]: any;
    };
};

type CreateAlbumParams = {
    name: string;
    socialProfileId?: SocialProfileType['id'];
    currentPostType?: PostTypesType | 'unselected';
    socialProfileType?: SocialProfileType['social_network_type'];
};

type CreateAlbumResponse = {
    id: number;
    label: string;
    value: number;
};

const CAMPAIGN_UNSUPPORTED_NETWORKS: SocialProfileNetworkType[] = [X, THREADS];

class CampaignService {
    isNetworkSupportedForCampaign = (network: SocialProfileNetworkType): boolean =>
        !CAMPAIGN_UNSUPPORTED_NETWORKS.includes(network);

    /**
     * Determines if the current post type is an Instagram story
     */
    isInstagramStoryPostType = (
        currentPostType?: CreateAlbumParams['currentPostType'],
        socialProfileType?: SocialProfileType['social_network_type']
    ): boolean => currentPostType === TYPE_STORY && socialProfileType === INSTAGRAM;

    /**
     * Validates if a campaign is compatible with the current filter based on post type
     */
    isCampaignValidForCurrentFilter(
        album: AlbumType | undefined,
        currentPostType?: CreateAlbumParams['currentPostType'],
        socialProfileType?: SocialProfileType['social_network_type']
    ): boolean {
        if (!album) return false;

        const isInstagramStory = this.isInstagramStoryPostType(currentPostType, socialProfileType);

        return isInstagramStory
            ? album.criteria?.album_type === 'stories'
            : album.criteria?.album_type !== 'stories';
    }

    /**
     * Determines the album type to filter on the API side based on the current post type
     */
    getAlbumTypeFilter(
        currentPostType?: CreateAlbumParams['currentPostType'],
        socialProfileType?: SocialProfileType['social_network_type']
    ): string | undefined {
        const isInstagramStory = this.isInstagramStoryPostType(currentPostType, socialProfileType);
        return isInstagramStory ? 'stories' : 'posts';
    }

    /**
     * Finds a selected album by its ID
     */
    findSelectedAlbum(
        campaignId: number,
        albums: { data: AlbumType[] }[] | undefined
    ): AlbumType | undefined {
        return albums?.[0]?.data?.find((album) => album.id === campaignId);
    }

    /**
     * Checks if a selected campaign is valid and should be cleaned up
     */
    shouldCleanupInvalidCampaign({
        selectedCampaignId,
        albums,
        currentPostType,
        socialProfileType
    }: {
        selectedCampaignId: number | null | undefined;
        albums: { data: AlbumType[] }[] | undefined;
        currentPostType?: PostTypesType;
        socialProfileType?: SocialProfileType['social_network_type'];
    }): boolean {
        if (!selectedCampaignId || !albums?.[0]?.data) return false;

        const selectedAlbum = this.findSelectedAlbum(selectedCampaignId, albums);
        return (
            !selectedAlbum ||
            !this.isCampaignValidForCurrentFilter(selectedAlbum, currentPostType, socialProfileType)
        );
    }

    /**
     * Generates the payload to create an album based on the post type
     */
    generateCreateAlbumPayload({
        name,
        socialProfileId,
        currentPostType,
        socialProfileType
    }: {
        name: string;
        socialProfileId?: SocialProfileType['id'];
        currentPostType?: CreateAlbumParams['currentPostType'];
        socialProfileType?: SocialProfileType['social_network_type'];
    }): {
        name: string;
        type: string;
        social_profile_id?: SocialProfileType['id'];
        criteria?: { album_type: string };
    } {
        const basePayload = {
            name,
            type: ALBUM_TYPE_MANUAL,
            social_profile_id: socialProfileId
        };

        if (this.isInstagramStoryPostType(currentPostType, socialProfileType)) {
            return { ...basePayload, criteria: { album_type: 'stories' } };
        }

        return { ...basePayload, criteria: { album_type: 'posts' } };
    }

    /**
     * Generates album options for the select component
     */
    generateAlbumOptions(albums: { data: AlbumType[] }[] | undefined): OptionType[] {
        return map(albums?.[0]?.data, (album) => ({
            id: album.id,
            label: album.name,
            value: album.id
        }));
    }

    handleCampaignSelect(
        selectedCampaign: OptionType | OptionType[] | undefined,
        currentTab: string | number,
        postsData: PostsData,
        setPostsData: (updater: (draft: PostsData) => void) => void
    ): void {
        const campaignId = (selectedCampaign as OptionType)?.id;

        if (currentTab === 'master') {
            Object.keys(postsData).forEach((postKey) => {
                setPostsData((draft) => {
                    draft[postKey].campaign_id = campaignId ? +campaignId : null;
                });
            });
        } else {
            setPostsData((draft) => {
                draft[currentTab].campaign_id = campaignId ? +campaignId : null;
            });
        }
    }

    /**
     * Creates a new album
     */
    async createAlbum(params: CreateAlbumParams): Promise<CreateAlbumResponse | undefined> {
        try {
            const payload = this.generateCreateAlbumPayload(params);

            const response = await post(ALBUMS, payload);

            return {
                id: response?.data?.id,
                label: response?.data?.name,
                value: response?.data?.id
            };
        } catch (error) {
            console.error('Create album error:', error);
            throw error;
        }
    }

    handleCreateAlbumError(error: any, t: (key: string) => string): string {
        if (error.status === 422) {
            return t('content:modal.update_album_error_name_unique');
        } else {
            return t('errors:error_occured_submit_data');
        }
    }
}

const campaignService = new CampaignService();
export { campaignService as CampaignService };
